import { Modal as AntdModal, Col, Row, Button as AntdButton, Checkbox } from "antd"
import { ArrowLeftOutlined } from "@ant-design/icons"
import { formatPhoneNumber } from "react-phone-number-input"

import { omit } from "lodash"
import { ReactNode, useEffect, useState } from "react"
import config from "react-global-configuration"
import { useDispatch } from "react-redux"

import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons } from "components/Card/Card"
import PreviousButton from "components/Previous/PreviousButton"
import { Title } from "components/Title/Title"
import { logout } from "containers/Login/services/actions"
import { cdn } from "core/cdn"
import { ModalProps } from "types/props"

import DOMPurify from "dompurify";

import styles from "./Modal.module.scss"
import { useLanguage } from "locales"

import { clearInterval, setInterval } from "worker-timers"
import { languages } from "locales/languages"

export const Modal: React.FC<ModalProps> = (props): JSX.Element => {
  const { languages } = useLanguage()
  const ErrorIcon = () => (
    <img
      src={cdn("images/devices/tutorial_device/attention.png")}
      alt="warning icon"
    />
  )

  const titleHeader = (
    <Row justify="center" wrap={false} style={{ marginTop: "50px" }} data-testid="modal-title-header">
      {props.iconError && (
        <Col flex="none">
          <ErrorIcon />
        </Col>
      )}
      <Col flex="auto">
        <Title text={props.title} {...props.titleProps} />
      </Col>
      {props.iconError && (
        <Col flex="none">
          <ErrorIcon />
        </Col>
      )}
    </Row>
  )

  const title = props.closable ? (
    <div data-testid="modal-title">
      <PreviousButton
        text={languages.close}
        onClick={() => {
          if (props.onClose) props.onClose()
          else if (props.onCancel) props.onCancel()
        }}
      />
      {props.title ? titleHeader : null}
    </div>
  ) : props.title ? (
    titleHeader
  ) : null
  return (
    <AntdModal
      data-testid="modal-root"
      width={props.style?.width || "100%"}
      className={styles.Modal}
      centered
      title={title}
      bodyStyle={props.bodyStyle}
      closable={false}
      // antd only fires onCancel from the mask, ESC and its own close icon
      // (disabled above); the footer buttons below never go through it.
      onCancel={props.onMaskClick}
      open={props.visible}
      style={props.style}
      footer={
        props.footer ? (
          <div data-testid="modal-footer">
            {props.footer}
          </div>
        ) : !props.noFooter ? (
          <Buttons data-testid="modal-footer">
            {props.onCancel && (
              <Button
                wide="long"
                type="link"
                onClick={props.onCancel}
                {...props.cancelButtonProps}
              >
                {props.cancelText || languages.previous}
              </Button>
            )}
            {props.onAccept && (
              <Button
                wide="long"
                loading={props.loading}
                disabled={props.loading}
                onClick={props.onAccept}
                {...props.acceptButtonProps}
              >
                {props.acceptText || languages.confirm}
              </Button>
            )}
          </Buttons>
        ) : null
      }
    >
      <div data-testid="modal-body">
        {props.children}
      </div>
    </AntdModal>
  )
}

export const GenericModal: React.FC<ModalProps & {EmailDesign?: boolean}> = (props): JSX.Element => {
  const { languages } = useLanguage()
  return (
  <Modal
    {...props}
    footer={
      props.footer ? (
        props.footer
      ) : !props.noFooter ? (
        <Buttons style = {{...props.buttonWrapperStyle, ...(props.EmailDesign && {display: "flex", flexDirection: 'column', gap: "5px"})}}>
          {props.onCancel && (
            <Button
              wide="long"
              {...props.cancelButtonProps}
              onClick={() => props.onCancel && props.onCancel()}
              {...(props.EmailDesign && {type:'primary'})}
            >
              {props.cancelText || languages.cancel}
            </Button>
          )}
          {props.onAccept && (
            <Button
              wide="long"
              danger
              type="primary"
              {...(props.EmailDesign && {type:'link', style: {textTransform: "none"}})}
              {...props.acceptButtonProps}
              onClick={() => props.onAccept && props.onAccept()}
            >
              {props.EmailDesign? <span style={{textDecoration: "underline"}}>{props.acceptText}</span> : props.acceptText}
            </Button>
          )}
        </Buttons>
      ) : undefined
    }
  >
    <div className="card-contenant font-size-large">
      {props.icon && (
        <div style={{ textAlign: "center", marginBottom: 60, marginTop: 45 }}>
          {typeof props.icon === "string" ? (
            <img src={props.icon || ""} alt="modal icon" />
          ) : (
            props.icon
          )}
        </div>
      )}
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(props.contentText, { USE_PROFILES: { html: true }}) || "",
        }}
      ></div>
      {props.children}
    </div>
  </Modal>
)}

export const ConsentModal = (props: {
  visible: boolean
  onAccept: (newsletterSubscription: boolean) => void
  onCancel: () => void
  hasValidEmail?: boolean
}): JSX.Element => {
  const { languages } = useLanguage()
  const [showCGU, setShowCGU] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [newsletterSubscription, setNewsletterSubscription] = useState(false)
  const [cguAccepted, setCguAccepted] = useState(false)
  const canOfferNewsletter = props.hasValidEmail !== false
  const showAcceptButton = !showPrivacy && !showCGU
  const canAccept = !canOfferNewsletter || cguAccepted
  const onAccept = showAcceptButton
    ? () => props.onAccept(canOfferNewsletter && newsletterSubscription)
    : undefined
  return (
    <GenericModal
      {...props}
      onAccept={onAccept}
      title={languages.cguAndPrivacyTitle}
      acceptText={canOfferNewsletter ? languages.continue : languages.IAcceptConsent}
      acceptButtonProps={{ danger: false, disabled: showAcceptButton && !canAccept }}
      cancelText={
        showCGU || showPrivacy ? languages.previous : languages.IRefuseConsent
      }
      onCancel={
        showCGU || showPrivacy
          ? () => {
              setShowCGU(false)
              setShowPrivacy(false)
            }
          : props.onCancel
      }
    >
      <div>
        <div
          style={
            canOfferNewsletter
              ? { textAlign: "left", display: "flex", alignItems: "flex-start", gap: 14 }
              : { textAlign: "center" }
          }
          hidden={showCGU || showPrivacy}
        >
          {canOfferNewsletter && (
            <Checkbox
              id="cgu-consent"
              checked={cguAccepted}
              onChange={(e) => setCguAccepted(e.target.checked)}
              style={{ marginTop: -4 }}
            />
          )}
          <label htmlFor={canOfferNewsletter ? "cgu-consent" : undefined}>
            {languages.IhaveReadAndAccept}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={(e) => { e.stopPropagation(); setShowCGU(true) }}>
            {languages.theCGU}
            </a>{languages.andThe}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              onClick={(e) => {
                e.stopPropagation()
                setShowPrivacy(true)
              }}
            >
              {languages.privacyPolicies}
            </a>
            {languages.ofMedadom}
          </label>
        </div>
        {canOfferNewsletter && (
          <div
            style={{ marginTop: 20, textAlign: "left", display: "flex", alignItems: "flex-start", gap: 14 }}
            hidden={showCGU || showPrivacy}
          >
            <Checkbox
              id="newsletter-subscription"
              checked={newsletterSubscription}
              onChange={(e) => setNewsletterSubscription(e.target.checked)}
              style={{ marginTop: -4 }}
            />
            <label htmlFor="newsletter-subscription">
              {languages.newsletterConsentCheckboxLabel}
            </label>
          </div>
        )}
        {showCGU &&
          <div
            className={styles.CGUContent}
          ><iframe src="https://info.medadom.com/cgu" style={{width: "-webkit-fill-available", height: "-webkit-fill-available"}} title="privacy_and_cgu" /> </div>
        }
        {showPrivacy &&
          <div hidden={!showPrivacy} className={styles.CGUContent}>
            <iframe src="https://info.medadom.com/politique-de-confidentialite" style={{width: "-webkit-fill-available", height: "-webkit-fill-available"}} title="privacy_and_cgu" />
          </div>
        }
      </div>
    </GenericModal>
  )
}

export const GenericCancelModal = (props: any): JSX.Element => {
  const { languages } = useLanguage()
  return (
  <AntdModal
    width="100%"
    className={styles.Modal}
    centered
    title={
      <div className={styles.iconTitle}>
        <img
          src={cdn("/icons/warning_icon.svg")}
          alt="generic_cancel_modal_icon"
        />
      </div>
    }
    closable={false}
    open={props.visible}
    footer={
      <Buttons>
        {props.onCancel && (
          <Button
            wide="long"
            onClick={props.onAccept}
            loading={props.loading}
            disabled={props.loading}
          >
              {props.onAcceptText || languages.continueConsultation}
          </Button>
        )}
        {props.onAccept && (
          <Button
            wide="long"
            type="primary"
            danger
            loading={props.loading}
            disabled={props.loading}
            onClick={props.onCancel}
          >
            {languages.cancelConsultation}
          </Button>
        )}
      </Buttons>
    }
  >
    <div className={styles.cancelConsultationContent}>
      {props.contentText || languages.cancel_warning}
    </div>
  </AntdModal>
)}

export const IdleModal = (props: any) => {
  const { languages } = useLanguage()
  const Initialseconds = config.get("disconnectTimer.seconds") || 30
  const InitialMinutes = config.get("disconnectTimer.minutes") || 1

  const dispatch = useDispatch()

  const [remainingSecond, setRemainingSecond] = useState(Initialseconds)
  const [remainingMinute, setRemainingMinute] = useState(InitialMinutes)
  const [timer, setTimer] = useState<any>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    clearInterval(timer)
    setRemainingMinute(InitialMinutes)
    setRemainingSecond(Initialseconds)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isIdle])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let sec = remainingSecond
    let min = remainingMinute
    if (props.visible) {
      const timer = setInterval(() => {
        sec = sec - 1 >= 0 ? sec - 1 : 59
        min = sec !== 59 ? min : min - 1
        if (!sec && !min) {
          timer && clearInterval(timer)
          props.onAccept()
          dispatch(logout())
        } else {
          setRemainingMinute(min)
          setRemainingSecond(sec)
        }
      }, 1000)
      setTimer(timer)
    } else {
      timer && clearInterval(timer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible])

  return (
    <GenericModal
      {...props}
      title={languages.inactivity}
      acceptText={languages.stayLogged}
    >
      <div style={{width: '100%', textAlign: 'center', fontSize: '28px'}}>{`${remainingMinute} m ${remainingSecond} ...`}</div>
    </GenericModal>
  )
}


const RightsCheckboxes = (props: {
  onCancel?: () => void
  onAccept: (value: string) => void
  relative?: boolean
}) => {
  const { languages } = useLanguage()
  const [right, setRight] = useState<string>()
  const MEDICAL_RIGHTS = [
    {
      content: (
        <span className="center">
          <b>{languages.c2s}</b>
          <br />
          (C2S / ex-CMU)
        </span>
      ),
      value: "C2S",
    },
    {
      content: (
        <span className="center">
          <b>{languages.ame}</b>
          <br />
          (AME)
        </span>
      ),
      value: "AME",
    },
    {
      content: (
        <span className="center">
          <b>{languages.ald}</b>
          <br />
          (ALD)
        </span>
      ),
      value: "ALD",
    },
  ]

  return (
    <>
      <Buttons style={{ paddingTop: "0" }}>
        {MEDICAL_RIGHTS.map((mRight, key) => (
          <FatButton
            key={key}
            clicked={mRight.value === right}
            onClick={() => setRight(mRight.value)}
          >
            {mRight.content}
          </FatButton>
        ))}
      </Buttons>
      <Buttons style={{ paddingTop: "100px" }}>
        <Button wide="long" type="primary" onClick={props.onCancel}>
          {languages.cancel}
        </Button>
        <Button
          disabled={!right}
          wide="long"
          onClick={() => right && props.onAccept(right)}
        >
          {languages.next}
        </Button>
      </Buttons>
    </>
  )
}

export const RightsModal: React.FC<ModalProps & { relative?: boolean }> = (
  props
): JSX.Element => {
  const { languages } = useLanguage()
  const [step, setStep] = useState<"check" | "attest">("check")
  const rightsSteps = [
    {
      step: "check",
      title: languages.indicateYourRights,
    },
    {
      step: "attest",
      title: languages.fraudIsPunished,
      contentText: languages.ameModalContent,
      acceptText: languages.ameModalAccept,
      cancelText: languages.ameModalCancel,
    },
  ]
  const rightsStepsForRelative = [
    {
      step: "check",
      title: languages.indicateRelativeRights,
    },
    {
      step: "attest",
      title: languages.fraudIsPunished,
      contentText: languages.ameModalContent,
      acceptText: languages.ameModalAccept,
      cancelText: languages.ameModalCancelRelative,
    },
  ]

  const content = props.relative
    ? (rightsStepsForRelative.find((s) => step === s.step) as any)
    : (rightsSteps.find((s) => step === s.step) as any)

  return (
    <GenericModal
      {...omit(props, "relative")}
      data-testid={`nir-rights-modal`}
      title={content.title}
      contentText={content.contentText}
      acceptText={content.acceptText}
      cancelText={content.cancelText}
      acceptButtonProps={{ wide: "default", type: "default", danger: false }}
      cancelButtonProps={{ wide: "default", type: "primary", danger: false }}
      noFooter={step === "check"}
      onCancel={() => {
        setStep("check")
        props.onCancel && props.onCancel()
      }}
    >
      {step === "check" ? (
        <RightsCheckboxes
          onCancel={props.onCancel}
          onAccept={() => setStep("attest")}
          relative={props.relative}
        />
      ) : undefined}
    </GenericModal>
  )
}

export const UploadModal: React.FC<ModalProps> = (props): JSX.Element => {
  const { languages } = useLanguage()
  const { onAccept, onCancel, title } = props

  const TitleBlock = (
    <div className={styles.title__block}>
      <div className={styles.title__icon}>
        <img src={cdn("images/warning_picto.svg")} alt="warning" />
      </div>
      <h1 className={styles.title__text}>{title? title : languages.UploadRecommendedText}</h1>
    </div>
  )

  return (
    <GenericModal
      {...props}
      bodyStyle={{ padding: 0 }}
      title={TitleBlock}
      acceptText={languages.UDModalTransmit}
      cancelText={languages.UDModalIgnore}
      acceptButtonProps={{ wide: "long", type: "primary", danger: false }}
      cancelButtonProps={{ wide: "long", type: "default", danger: true }}
      onCancel={onCancel}
      onAccept={onAccept}
    />
  )
}


const PreviousCloseIcon = (): JSX.Element => {
  const { languages } = useLanguage()
  return (
    <AntdButton type="default" style={{ borderRadius: '0px', fontSize: '18px', lineHeight: 1, height: '60px', color: '#211452', borderColor: '#211452', textTransform: 'uppercase' }}> <ArrowLeftOutlined /> {languages.previous}</AntdButton>
  )
}

interface PhoneStatusModalProps {
  visible: boolean
  onAccept: () => void
  onClose: () => void
  phoneNumber: string
}

const PhoneStatusModal = ({
  visible,
  onAccept,
  onClose,
  phoneNumber,
  contentText,
  acceptText,
}: PhoneStatusModalProps & {
  contentText: ReactNode
  acceptText: string
}): JSX.Element => {
  const { languages } = useLanguage()
  return (
    <AntdModal
      width="70%"
      className={styles.ExistingPhoneModal}
      centered
      title={<Title text={<img src={cdn('/images/warning_picto.svg')} alt="warning" />} />}
      closable={true}
      closeIcon={<PreviousCloseIcon />}
      onCancel={onClose}
      open={visible}
      footer={false}
    >
      <div className={styles.ExistingPhoneModalContent}>
        <div>{contentText}</div>
        <div className={styles.ExistingPhoneInfo}>{formatPhoneNumber(phoneNumber)}</div>
      </div>
      <Buttons style={{ marginTop: '66px' }} direction="vertical" size={40}>
        <Button wide="long" style={{ fontSize: '28px', fontWeight: 'bold' }} type='primary' onClick={onAccept}>{acceptText}</Button>
        <Button wide="long" type="link" style={{ fontSize: '28px', color: '#1D71B8' }} onClick={onClose}><u>{languages.modifyPhoneNumber}</u></Button>
      </Buttons>
    </AntdModal>
  )
}

export const CancelSignupModal = ({
  visible,
  onCancelSignup,
  onModifyPhone,
  onClose,
}: {
  visible: boolean
  onCancelSignup: () => void
  onModifyPhone: () => void
  onClose: () => void
}): JSX.Element => {
  const { languages } = useLanguage()
  return (
    <AntdModal
      width="70%"
      className={styles.ExistingPhoneModal}
      centered
      title={<Title text={<img src={cdn('/images/warning_picto.svg')} alt="warning" />} />}
      closable={true}
      closeIcon={<PreviousCloseIcon />}
      onCancel={onClose}
      open={visible}
      footer={false}
    >
      <div className={styles.ExistingPhoneModalContent} style={{ fontSize: '48px', lineHeight: 1.3 }}>
        {languages.cancelSignupQuestion_1}
        <b>{languages.cancelSignupQuestion_bold1}</b>
        <br />
        {languages.cancelSignupQuestion_2}
        <b>{languages.cancelSignupQuestion_bold2}</b>
        {languages.cancelSignupQuestion_3}
      </div>
      <Buttons style={{ paddingTop: '50px' }} size={40}>
        <Button style={{ width: '560px', height: '110px', fontSize: '38px', color: '#211452', borderColor: '#211452' }} type="default" onClick={onModifyPhone}>{languages.modifyMyNumber}</Button>
        <Button style={{ width: '560px', height: '110px', fontSize: '38px' }} type="primary" danger onClick={onCancelSignup}>{languages.cancelMySignup}</Button>
      </Buttons>
    </AntdModal>
  )
}

export const ExistingPhoneModal = (props: PhoneStatusModalProps): JSX.Element => {
  const { languages } = useLanguage()
  return (
    <PhoneStatusModal
      {...props}
      contentText={
        <>
          {languages.phoneAlreadyExist_1}
          <b>{languages.phoneAlreadyExist_bold1}</b>
          {languages.phoneAlreadyExist_2}
          <b>{languages.phoneAlreadyExist_bold2}</b>
          {languages.phoneAlreadyExist_3}
        </>
      }
      acceptText={languages.logMeIn}
    />
  )
}

export const NonExistingPhoneModal = (props: PhoneStatusModalProps): JSX.Element => {
  const { languages } = useLanguage()
  return (
    <PhoneStatusModal
      {...props}
      contentText={
        <>
          <b>{languages.phoneDoesNotExist_bold1}</b>
          {languages.phoneDoesNotExist_1}
          <b>{languages.phoneDoesNotExist_bold2}</b>
        </>
      }
      acceptText={languages.signMeUp}
    />
  )
}

export const TreatingDoctorIncitationModal = ({
  visible,
  onEnterDoctor,
  onSkip,
}: {
  visible: boolean
  onEnterDoctor: () => void
  onSkip: () => void
}): JSX.Element => {
  const { languages } = useLanguage()

  const TitleBlock = (
    <div className={styles.title__block}>
      <div className={styles.title__icon}>
        <img src={cdn("images/warning_picto.svg")} alt="warning" />
      </div>
      <h1 className={styles.title__text}>{languages.indicateTreatingDoctorModal}</h1>
    </div>
  )

  return (
    <GenericModal
      visible={visible}
      bodyStyle={{ padding: 0 }}
      title={TitleBlock}
      acceptText={languages.enterMyDoctor}
      cancelText={languages.skipStep}
      acceptButtonProps={{ wide: "long", type: "primary", danger: false }}
      cancelButtonProps={{ wide: "long", danger: true }}
      onAccept={onEnterDoctor}
      onCancel={onSkip}
    />
  )
}

// The wording only carries <b> and <br />: build React nodes instead of
// injecting HTML, so the text can never reach the DOM as markup.
const renderBoldLines = (text: string): ReactNode[] =>
  text.split(/(<br\s*\/?>|<b>.*?<\/b>)/).map((part, i) => {
    if (/^<br\s*\/?>$/.test(part)) return <br key={i} />
    const bold = part.match(/^<b>(.*?)<\/b>$/)
    return bold ? <b key={i}>{bold[1]}</b> : part
  })

export const MediaProblemModal = ({
  visible,
  onClose,
}: {
  visible: boolean
  onClose: () => void
}): JSX.Element => {
  const { languages } = useLanguage()

  const TitleBlock = (
    <div className={styles.mediaProblem__block}>
      <img src={cdn("images/warning_picto.svg")} alt="warning" />
      <p className={styles.mediaProblem__text}>
        {renderBoldLines(languages.mediaProblemDetected)}
      </p>
    </div>
  )

  return (
    <GenericModal
      visible={visible}
      closable
      onClose={onClose}
      style={{ width: 1246 }}
      bodyStyle={{ padding: 0 }}
      title={TitleBlock}
      // TitleBlock is a fresh element each render: TextTransition would replay the slide on every call-poll re-render
      titleProps={{
        doNotAnimateTitle: true,
        // Title's default margins: the mockup wants 45px between text and CTA
        style: { marginTop: -20, marginBottom: 20 },
      }}
      footer={
        <Buttons style={{ marginTop: 0, paddingTop: 0 }}>
          <Button wide="long" type="primary" onClick={() => window.location.reload()}>
            {languages.refreshPage}
          </Button>
        </Buttons>
      }
    />
  )
}

export const IneligibilityModal = ({title, visible, onCancel, onClose}: {title: string | JSX.Element, visible: boolean, onCancel: () => void, onClose: () => void}) => <Modal
title={title} visible={visible} onCancel={onCancel} cancelButtonProps={{ type: 'primary'}} cancelText={languages.returnHomePage} closable onClose={onClose}>
  <div className={styles.IneligibilityCard}>
    <div className={styles.IneligibilityContent}>
      <div>
        <b>{languages.ageNotInRange}</b>{" "}{languages.forThisCourse}
      </div>
      <p>{languages.courseReservedFor}</p>
      <ul>
        <li><b>{languages.eighteenToTwentyFiveYears}</b></li>
        <li><b>{languages.fortyFiveToFifty}</b></li>
        <li><b>{languages.sixtyToSixtyFive}</b></li>
        <li><b>{languages.seventyToSeventyFive}</b></li>
      </ul>
      <p>{languages.thanksForYourInterest}</p>
    </div>
  </div>
</Modal>